import React, { useEffect, useMemo } from 'react'

import { isDev } from '@common/config/env'
import useToastSurface from '@common/hooks/useToastSurface'
import { Portal } from '@gorhom/portal'

import ToastList from './ToastList'
import { ToastListContext } from './toastListContext'
import { ToastAction, ToastOptions, Toast } from './types'
import useToastQueue from './useToastQueue'

export type { Toast, ToastAction, ToastOptions }

interface Props {
  children: React.ReactNode | React.ReactNode[]
}

const ToastContext = React.createContext<{
  addToast: (text: string, options?: ToastOptions) => number
  removeToast: (id: number) => void
}>({
  addToast: () => -1,
  removeToast: () => {}
})

const ToastProvider = ({ children }: Props) => {
  const { anchor, maxVisible } = useToastSurface()
  const { visible, addToast, dismiss } = useToastQueue(maxVisible)

  // Dev-only console harness for exercising the queue:
  //   __zkxToast.burst(6) / .card() / .dup() / .sticky()
  useEffect(() => {
    if (!isDev || typeof window === 'undefined') return

    const types = ['success', 'error', 'info', 'warning'] as const

    ;(window as any).__zkxToast = {
      add: addToast,
      burst: (n = 6) =>
        Array.from({ length: n }, (_, i) =>
          addToast(`Toast #${i + 1} — ${Date.now()}`, { type: types[i % types.length] })
        ),
      card: () =>
        addToast('Private proof could not be completed', {
          type: 'error',
          title: 'Private proof could not be completed',
          description: 'Your funds are safe. Check your connection and try again.',
          // eslint-disable-next-line no-console
          action: { text: 'Retry', onPress: () => console.log('[toast] retry pressed') }
        }),
      dup: () => {
        for (let i = 0; i < 5; i++) addToast('Same message')
      },
      // Replays the POI cadence: alternating wordings, ~600ms apart. Pass a
      // falsy groupId to see the churn a shared group prevents.
      seq: (groupId: string | null = 'railgun-ppoi') => {
        const steps = [
          'Private Proof of Innocence: loading next batch…',
          'Generating Private Proof of Innocence (2 proofs)… Keep the app open while processing.',
          'Private Proof of Innocence: loading next batch…',
          'Generating Private Proof of Innocence (1 proof)… Keep the app open while processing.',
          'Private Proof of Innocence: synchronization completed'
        ]
        steps.forEach((s, i) =>
          setTimeout(
            () =>
              addToast(s, {
                type: i === steps.length - 1 ? 'success' : 'info',
                ...(groupId ? { groupId } : {})
              }),
            i * 600
          )
        )
      },
      sticky: () => addToast('Sticky toast', { sticky: true })
    }

    return () => {
      delete (window as any).__zkxToast
    }
  }, [addToast])

  // A referentially stable child keeps Portal from re-registering the node on
  // every toast — Portal is memoized on [children].
  const portalChild = useMemo(() => <ToastList />, [])

  return (
    <ToastContext.Provider
      value={useMemo(
        () => ({
          addToast,
          removeToast: dismiss
        }),
        [addToast, dismiss]
      )}
    >
      <ToastListContext.Provider
        value={useMemo(() => ({ visible, dismiss, anchor }), [visible, dismiss, anchor])}
      >
        <Portal hostName="global">{portalChild}</Portal>
        {children}
      </ToastListContext.Provider>
    </ToastContext.Provider>
  )
}

export { ToastContext, ToastProvider }
